import { useNavigate } from 'react-router-dom';
import { useFormContext, useForm } from 'react-hook-form';

import {
    type IPlaybookV2,
    EPlaybookAction,
    EPlaybookFilter,
    EPlaybookStepTypeGroup,
    EPlaybookTrigger,
    useCreatePlaybookV2,
    useUpdatePlaybook,
    useUpdatePlaybookV2,
} from 'src/services/api';
import type { IPlaybookStep, EPlaybookTriggerFilter } from 'src/services/api/playbooks/types';
import { useNotifications } from 'src/notifications';
import { type IPlaybookTemplate } from 'src/pages/landlord/playbooks/new/constants';
import { LIBRARY_PLAYBOOKS_MAP } from 'src/pages/landlord/playbooks/new/playbook-library-data';
import { formatPlaybookMessageForBackend } from 'src/common/playbook-variables/playbook-variable-utils';
import {
    getUpdatePlaybookLeadFilterParams,
    getUpdatePlaybookTrigger,
    getUpdatePlaybookTriggerParams,
} from 'src/pages/landlord/playbooks/components/editor/update-playbook-payload';

function buildStepChain(filters: EPlaybookTriggerFilter[], actionStep: IPlaybookStep): IPlaybookStep {
    let step: IPlaybookStep = actionStep;
    for (let i = filters.length - 1; i >= 0; i--) {
        step = {
            type_group: EPlaybookStepTypeGroup.TriggerFilter,
            type: filters[i],
            params: null as never,
            children: [{ step }],
        };
    }
    return step;
}

export type IPlaybookV2Form = {
    title: string;
    description?: string;
    trigger?: IPlaybookV2['trigger'];
    first_step?: IPlaybookV2['first_step'];
}

const getDefaultValues = (
    playbook: IPlaybookV2 | undefined,
    playbookTemplate: IPlaybookTemplate | undefined,
): IPlaybookV2Form | undefined => {
    if (playbook) {
        return {
            title: playbook.title,
            description: playbook.description,
            trigger: playbook.trigger,
            first_step: playbook.first_step,
        };
    }

    if (playbookTemplate) {
        // Library templates (IDs prefixed 'lib-') — checked before the legacy switch
        const libTemplate = LIBRARY_PLAYBOOKS_MAP.get(playbookTemplate);
        if (libTemplate) {
            const actionStep: IPlaybookStep = {
                type_group: EPlaybookStepTypeGroup.Action,
                type: EPlaybookAction.SendMessage,
                params: {
                    channel: 'sms',
                    message: libTemplate.message,
                },
                children: null,
            };
            return {
                title: libTemplate.title,
                trigger: libTemplate.trigger,
                first_step: libTemplate.filters?.length ?
                    buildStepChain(libTemplate.filters, actionStep) :
                    actionStep,
            };
        }

        switch (playbookTemplate) {
            case 'welcome-message': {
                return {
                    title: 'Welcome Message',
                    trigger: {
                        type: EPlaybookTrigger.LeadIngested,
                        run_limit_per_lead: 1,
                    },
                    first_step: {
                        type_group: EPlaybookStepTypeGroup.Action,
                        type: EPlaybookAction.SendMessage,
                        params: {
                            channel: 'sms',
                            message: '',
                        },
                        children: null,
                    },
                };
            }

            case 'follow-up-message': {
                return {
                    title: 'Follow Up Message',
                    trigger: {
                        type: EPlaybookTrigger.LeadUnresponsive,
                        run_limit_per_lead: 1,
                        params: {
                            time_unit: 'days',
                            time_value: 10,
                        },
                    },
                    first_step: {
                        type_group: EPlaybookStepTypeGroup.Action,
                        type: EPlaybookAction.SendMessage,
                        params: {
                            channel: 'sms',
                            message: '',
                        },
                        children: null,
                    },
                };
            }

            case 'tour-confirmaion-message': {
                return {
                    title: 'Tour Confirmation Message',
                    trigger: {
                        type: EPlaybookTrigger.TourStarted,
                        run_limit_per_lead: 1,
                        params: {
                            time_offset_type: 'before',
                            time_offset_unit: 'hours',
                            time_offset_value: 2,
                        },
                    },
                    first_step: {
                        type_group: EPlaybookStepTypeGroup.Action,
                        type: EPlaybookAction.SendMessage,
                        params: {
                            channel: 'sms',
                            message: '',
                        },
                        children: null,
                    },
                };
            }

            case 'tour-follow-up-message': {
                return {
                    title: 'Tour Follow Up Message',
                    trigger: {
                        type: EPlaybookTrigger.TourStarted,
                        run_limit_per_lead: 1,
                        params: {
                            time_offset_type: 'after',
                            time_offset_unit: 'hours',
                            time_offset_value: 4,
                        },
                    },
                    first_step: {
                        type_group: EPlaybookStepTypeGroup.Action,
                        type: EPlaybookAction.SendMessage,
                        params: {
                            channel: 'sms',
                            message: '',
                        },
                        children: null,
                    },
                };
            }

            case 'open-house-message': {
                return {
                    title: 'Open House Message',
                    trigger: {
                        type: EPlaybookTrigger.Manual,
                        run_limit_per_lead: 1,
                    },
                    first_step: {
                        type_group: EPlaybookStepTypeGroup.Filter,
                        type: EPlaybookFilter.SpecificProperty,
                        params: {
                            // @ts-expect-error default value
                            property_id: undefined,
                        },
                        children: [
                            {
                                'step': {
                                    type_group: EPlaybookStepTypeGroup.Action,
                                    type: EPlaybookAction.SendMessage,
                                    params: {
                                        channel: 'sms',
                                        message: '',
                                    },
                                    children: null,
                                }
                            }
                        ],
                    },

                };
            }

            default: {
                return undefined;
            }
        }
    }

    return undefined;
};

export const usePlaybookForm = (
    playbook: IPlaybookV2 | undefined,
    playbookTemplate: IPlaybookTemplate | undefined,
) => {
    const navigate = useNavigate();
    const { addNotification } = useNotifications();

    const { mutateAsync: createPlaybook } = useCreatePlaybookV2();
    const { mutateAsync: updatePlaybook } = useUpdatePlaybook(playbook?.uuid);
    const { mutateAsync: updatePlaybookV2 } = useUpdatePlaybookV2(playbook?.uuid);

    const form = useForm<IPlaybookV2>({
        mode: 'onBlur',
        defaultValues: getDefaultValues(playbook, playbookTemplate),
    });

    const submit = async (values: IPlaybookV2) => {
        if (!values.trigger) {
            addNotification('Trigger is required', 'error');
            return;
        }

        const actionStep = getStep('action', values.first_step) as Extract<IPlaybookV2['first_step'], { type_group: EPlaybookStepTypeGroup.Action }> | undefined;
        if (!actionStep) {
            addNotification('Action is required', 'error');
            return;
        }
        if (actionStep.type != EPlaybookAction.SendMessage && actionStep.type !== EPlaybookAction.UpdateLabels) {
            (actionStep as { type: EPlaybookAction }).type = EPlaybookAction.SendMessage;
            delete (actionStep.params as Record<string, unknown>).snippet;
        }
        if (actionStep.type !== EPlaybookAction.UpdateLabels) {
            if (!actionStep.params.message) {
                addNotification('Message is required', 'error');
                return;
            }
            actionStep.params.message = formatPlaybookMessageForBackend(actionStep.params.message);
        }

        if (playbook) {
            const trigger = getUpdatePlaybookTrigger(values.trigger);
            await updatePlaybookV2({
                first_step: values.first_step,
            });
            const leadFilterParams = getUpdatePlaybookLeadFilterParams(values.first_step, trigger);
            // Update the playbook title only if updating the steps was successful
            await updatePlaybook({
                title: values.title,
                property_id: leadFilterParams?.property_id,
                lead_filter_params: leadFilterParams,
                trigger_params: getUpdatePlaybookTriggerParams(trigger)
            });
        } else {
            await createPlaybook({ playbook: values });
            navigate('/landlord/inner/playbooks');
        }
    };

    return { form, submit };
};

export const usePlaybookFormContext = () => {
    return useFormContext<IPlaybookV2Form>();
};

export const getStep = (typeGroup: string, firstStep?: IPlaybookV2['first_step']) => {
    let currentStep = firstStep;

    while (currentStep) {
        if (currentStep.type_group === typeGroup) {
            return currentStep;
        }
        currentStep = currentStep.children?.[0].step;
    }
};

export const getLastStep = (firstStep?: IPlaybookV2['first_step']) => {
    let currentStep = firstStep;
    let lastStep = firstStep;

    while (currentStep?.children?.[0]?.step) {
        lastStep = currentStep.children[0].step;
        currentStep = lastStep;
    }

    return lastStep;
};
